import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { BreadcrumbItem } from '../../../domain/src/lib/breadcrumb.model';

export interface MenuItemData {
  label: string;
  route?: string;
  icon?: string;
  url?: string;
}

export type BreadcrumbModule =
  | 'accounting'
  | 'treasury'
  | 'budget'
  | 'vault'
  | 'invoicing'
  | 'maestros'
  | 'products'
  | 'company';

@Injectable({ providedIn: 'root' })
export class BreadcrumbSyncService {
  readonly breadcrumbSubject = new Subject<BreadcrumbItem[]>();
  readonly breadcrumb$: Observable<BreadcrumbItem[]> = this.breadcrumbSubject.asObservable();

  private currentItems: BreadcrumbItem[] = [];

  update(items: BreadcrumbItem[]): void {
    this.currentItems = items;
    this.breadcrumbSubject.next(items);
  }

  getCurrent(): BreadcrumbItem[] {
    return [...this.currentItems];
  }

  clear(): void {
    this.currentItems = [];
    this.breadcrumbSubject.next([]);
  }

  createFromTab(
    tabTitle: string,
    tabRoute: string,
    module: BreadcrumbModule,
    icon?: string,
  ): BreadcrumbItem[] {
    const { moduleLabel, modulePath } = this.getModuleConfig(module);

    const baseBreadcrumb: BreadcrumbItem = {
      label: moduleLabel,
      url: `/layout/${modulePath}`,
      icon: 'Folder',
    };

    const itemBreadcrumb: BreadcrumbItem = {
      label: tabTitle,
      url: this.buildRoute(tabRoute, modulePath),
      icon: icon || 'File',
    };

    return [baseBreadcrumb, itemBreadcrumb];
  }

  createFromMenuItem(menuItem: MenuItemData, module: BreadcrumbModule): BreadcrumbItem[] {
    const { moduleLabel, modulePath } = this.getModuleConfig(module);

    const breadcrumbs: BreadcrumbItem[] = [
      {
        label: moduleLabel,
        url: `/layout/${modulePath}`,
        icon: 'Folder',
      },
    ];

    if (menuItem.route) {
      breadcrumbs.push({
        label: menuItem.label,
        url: this.buildRoute(menuItem.route, modulePath),
        icon: menuItem.icon || 'File',
      });
    }

    return breadcrumbs;
  }

  private getModuleConfig(module: BreadcrumbModule): { moduleLabel: string; modulePath: string } {
    const config: Record<BreadcrumbModule, { label: string; path: string }> = {
      accounting: { label: 'Contabilidad', path: 'accounting' },
      treasury: { label: 'Tesorería', path: 'treasury' },
      budget: { label: 'Presupuesto', path: 'budget' },
      vault: { label: 'Vault', path: 'vault' },
      invoicing: { label: 'Facturación', path: 'invoicing' },
      maestros: { label: 'Maestros', path: 'maestros' },
      products: { label: 'Productos', path: 'products' },
      company: { label: 'Compañía', path: 'company' },
    };

    const { label, path } = config[module];
    return { moduleLabel: label, modulePath: path };
  }

  private buildRoute(route: string, modulePath: string): string {
    if (route.startsWith('/')) return route;
    return `/layout/${modulePath}/${route}`;
  }
}
